import { useModalContext } from '../../../context/Modal';
import { DatePicker } from '../../form';
import { FooterNavigator } from '../../Footer/FooterNavigator';
import { IAsset, IAssetAlert } from '../../../types/model';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Input } from '../../form';
import { formatNumber } from '../../../helpers/functions';
import { CREATE_ASSET_ALERT } from '../../../http/apollo-queries';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import React from 'react';

interface AddAlertProps {
  asset: IAsset;
}

export const AddAlert: React.FC<AddAlertProps> = ({ asset }) => {
  const { t } = useTranslation();
  const { closeModal } = useModalContext();

  const [createAssetAlert, { loading }] = useMutation(CREATE_ASSET_ALERT);
  const currentPrice: number = asset.history?.[0]?.cur || 0;

  const formik = useFormik({
    initialValues: {
      assetName: asset.name,
      alertName: '',
      expireAt: moment().add(1, 'months').toDate(),
      currency: asset.currency,
      priceThreshold: '',
    },

    onSubmit({ expireAt, alertName, priceThreshold }) {
      return createAssetAlert({
        variables: {
          assetId: asset.id,
          expireAt,
          name: alertName,
          value: Number(priceThreshold),
        },
      }).finally(closeModal);
    },
  });

  const isInvalidForm = !formik.values.priceThreshold;

  return (
    <FormikProvider value={formik}>
      <form
        className={'flex flex-col gap-12 py-10 px-20'}
        autoComplete={'off'}
        onSubmit={formik.handleSubmit}
      >
        <div>
          <Input
            size={'full'}
            name={'assetName'}
            id={'assetName'}
            type={'text'}
            label={t('alerts.addAlert.assetName')}
            placeholder={t('alerts.addAlert.assetName')}
            formik={formik}
            readOnly
            isDisabled
          />
        </div>

        <div className="flex justify-between gap-5">
          <Input
            size={'full'}
            name={'alertName'}
            id={'alertName'}
            type={'text'}
            label={t('alerts.addAlert.notificationName')}
            placeholder={t('alerts.addAlert.notificationName')}
            formik={formik}
          />
          <DatePicker
            size="full"
            id={'alertDate'}
            date={formik.values.expireAt}
            onChange={(date) => formik.setFieldValue('expireAt', date)}
          />
        </div>

        <div className="flex gap-5">
          <Input
            size="medium"
            name="currency"
            id="currency"
            formik={formik}
            type="text"
            isDisabled={true}
            placeholder={t('alerts.addAlert.currency')}
            label={t('alerts.addAlert.currency')}
            value={asset.currency}
          />
          <Input
            size="medium"
            name="priceThreshold"
            id="priceThreshold"
            formik={formik}
            type="number"
            placeholder={t('alerts.addAlert.priceThreshold')}
            label={t('alerts.addAlert.priceThreshold')}
            assistiveText={t(`alerts.currentValue`, {
              value: formatNumber(currentPrice),
              currency: asset.currency,
            })}
            validate={(val) => {
              const error: any = {};
              if (!val) {
                error.invalid = true;
              }
              return Object.keys(error).length !== 0 ? error : null;
            }}
          />
        </div>

        {/* footer */}
        <FooterNavigator
          loading={loading}
          isDisabledSaveButton={loading || isInvalidForm}
          saveButtonName="Save"
          onCancel={closeModal}
          onSave={formik.handleSubmit}
        />
      </form>
    </FormikProvider>
  );
};
